import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Card, Button, Upload, Table, Space, Tag, Steps, Progress,
  Statistic, Row, Col, Radio, message, Alert, Descriptions, Result, Spin,
} from 'antd';
import {
  InboxOutlined, ArrowLeftOutlined, CloudUploadOutlined,
  CheckCircleOutlined, LoadingOutlined, ClockCircleOutlined,
} from '@ant-design/icons';
import {
  getCourse, uploadFiles, prescanFile, executeImport, getImportHistory,
} from '../services/api';

const { Dragger } = Upload;

export default function CourseImportPage() {
  const { courseKey } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [scanResult, setScanResult] = useState(null);
  const [importMode, setImportMode] = useState('append');
  const [importing, setImporting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    fetchCourse();
    fetchHistory();
  }, [courseKey]);

  const fetchCourse = async () => {
    try {
      const res = await getCourse(courseKey);
      setCourse(res.data.data);
    } catch {
      message.error('Course not found');
      navigate('/courses');
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await getImportHistory(courseKey);
      setHistory(res.data.data || []);
    } catch { /* ignore */ }
  };

  // Step 1: Upload files
  const handleUpload = async (info) => {
    // Only proceed if files are being added (info.file.status is usually undefined or 'uploading' with beforeUpload: false)
    if (info.file.status === 'removed') return;

    const formData = new FormData();
    info.fileList.forEach((f) => {
      if (f.originFileObj) formData.append('files', f.originFileObj);
    });

    if (formData.getAll('files').length === 0) return;

    setScanning(true);
    try {
      const res = await uploadFiles(formData);
      setUploadedFiles(res.data.data);

      // Auto prescan first file
      if (res.data.data.length > 0) {
        const file = res.data.data[0];
        const scanRes = await prescanFile(file.savedPath, file.fileType);
        setScanResult({
          ...scanRes.data.data,
          filePath: file.savedPath,
          fileType: file.fileType,
          fileName: file.originalName,
          fileSize: file.size,
        });
        setCurrentStep(1);
        message.success(`${res.data.data.length} file(s) scanned`);
      }
    } catch (err) {
      message.error('Upload or scan failed');
    } finally {
      setScanning(false);
    }
  };

  // Step 3: Execute import
  const handleImport = async () => {
    if (!scanResult) return;
    setImporting(true);
    setCurrentStep(2);

    try {
      const res = await executeImport({
        filePath: scanResult.filePath,
        fileType: scanResult.fileType,
        courseKey: parseInt(courseKey),
        importMode,
      });

      setImportResult(res.data.data);
      setCurrentStep(3);
      fetchCourse();
      fetchHistory();
      message.success('Import completed!');
    } catch (err) {
      message.error('Import failed: ' + (err.response?.data?.error || err.message));
      setCurrentStep(1);
    }
    setImporting(false);
  };

  const matchedEvents = scanResult?.counts?.[course?.course_id] || 0;
  const totalEvents = scanResult?.totalLines || 0;
  const otherEvents = totalEvents - matchedEvents - (scanResult?.errorLines || 0);

  const historyColumns = [
    {
      title: 'Date', dataIndex: 'created_at', key: 'date',
      render: (v) => new Date(v).toLocaleString('th-TH')
    },
    { title: 'File', dataIndex: 'filename', key: 'file' },
    { title: 'Imported', dataIndex: 'imported_events', key: 'imported' },
    { title: 'Skipped', dataIndex: 'skipped_events', key: 'skipped' },
    { title: 'Errors', dataIndex: 'error_events', key: 'errors' },
    {
      title: 'Status', dataIndex: 'status', key: 'status',
      render: (s) => (
        <Tag color={s === 'completed' ? 'green' : s === 'failed' ? 'red' : 'orange'}>
          {s}
        </Tag>
      ),
    },
  ];

  if (!course) return null;

  return (
    <div>
      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/courses')}
        style={{ marginBottom: 16 }}>
        Back to Courses
      </Button>

      <h2>Import Logs: {course.display_name || course.course_id}</h2>
      <p style={{ color: '#888' }}>Course ID: {course.course_id}</p>

      {/* Current course stats */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={24}>
          <Col span={6}>
            <Statistic title="Total Events" value={course.stats?.total_events || 0} />
          </Col>
          <Col span={6}>
            <Statistic title="Learners" value={course.stats?.total_learners || 0} />
          </Col>
          <Col span={6}>
            <Statistic title="First Event"
              value={course.stats?.first_event
                ? new Date(course.stats.first_event).toLocaleDateString('th-TH') : '-'} />
          </Col>
          <Col span={6}>
            <Statistic title="Last Event"
              value={course.stats?.last_event
                ? new Date(course.stats.last_event).toLocaleDateString('th-TH') : '-'} />
          </Col>
        </Row>
        {course.breakdown && course.breakdown.length > 0 && (
          <Row gutter={16} style={{ marginTop: 16 }}>
            {course.breakdown.map((b) => (
              <Col key={b.event_category}>
                <Tag>{b.event_category}: {b.count}</Tag>
              </Col>
            ))}
          </Row>
        )}
      </Card>

      {/* Import Steps */}
      <Steps current={currentStep} style={{ marginBottom: 32 }}
        items={[
          { title: 'Upload File' },
          { title: 'Preview & Settings' },
          { title: 'Importing' },
          { title: 'Complete' },
        ]}
      />

      {/* Step 0: Upload */}
      {currentStep === 0 && (
        <Card>
          <Alert
            message={`Events will be filtered for: ${course.course_id}`}
            description="Only events matching this Course ID will be imported. Other courses in the file will be skipped."
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />
          <Spin spinning={scanning} tip="Uploading and scanning file...">
            <Dragger
              multiple
              accept=".log,.json,.csv"
              beforeUpload={() => false}
              onChange={handleUpload}
              showUploadList={false}
              disabled={scanning}
            >
              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
              <p className="ant-upload-text">Drag & Drop tracking log files here</p>
              <p className="ant-upload-hint">Supports .log, .json, .csv (max 500MB per file)</p>
            </Dragger>
          </Spin>
        </Card>
      )}

      {/* Step 1: Preview */}
      {currentStep === 1 && scanResult && (
        <Card>
          <h3>Scan Results: {scanResult.fileName}</h3>
          <Descriptions bordered column={2} style={{ marginBottom: 24 }}>
            <Descriptions.Item label="File">
              {scanResult.fileName}
            </Descriptions.Item>
            <Descriptions.Item label="Size">
              {(scanResult.fileSize / 1024 / 1024).toFixed(1)} MB
            </Descriptions.Item>
            <Descriptions.Item label="Total Events in File">
              {totalEvents.toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Parse Errors">
              {scanResult.errorLines || 0}
            </Descriptions.Item>
          </Descriptions>

          <Row gutter={24} style={{ marginBottom: 24 }}>
            <Col span={8}>
              <Card>
                <Statistic
                  title={`Match: ${course.course_id}`}
                  value={matchedEvents.toLocaleString()}
                  suffix="events"
                  valueStyle={{ color: '#3f8600' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Other Courses (skip)"
                  value={otherEvents.toLocaleString()}
                  suffix="events"
                  valueStyle={{ color: '#999' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Match Rate"
                  value={totalEvents > 0 ? ((matchedEvents / totalEvents) * 100).toFixed(1) : 0}
                  suffix="%"
                />
              </Card>
            </Col>
          </Row>

          <h4>Import Mode</h4>
          <Radio.Group value={importMode} onChange={(e) => setImportMode(e.target.value)}
            style={{ marginBottom: 24 }}>
            <Radio.Button value="append">Append (Add New)</Radio.Button>
            <Radio.Button value="skip_duplicates">Skip Duplicates</Radio.Button>
          </Radio.Group>

          <div>
            <Space>
              <Button onClick={() => { setCurrentStep(0); setScanResult(null); }}>
                Back
              </Button>
              <Button
                type="primary"
                size="large"
                icon={<CloudUploadOutlined />}
                onClick={handleImport}
                disabled={matchedEvents === 0}
              >
                Import {matchedEvents.toLocaleString()} events
              </Button>
            </Space>
          </div>
        </Card>
      )}

      {/* Step 2: Importing */}
      {currentStep === 2 && (
        <Card>
          <div style={{ textAlign: 'center', padding: 40 }}>
            <LoadingOutlined style={{ fontSize: 48, color: '#1890ff' }} />
            <h3 style={{ marginTop: 24 }}>Importing...</h3>
            <p>Processing {matchedEvents.toLocaleString()} events for {course.course_id}</p>
            <Progress status="active" percent={99} />
          </div>
        </Card>
      )}

      {/* Step 3: Complete */}
      {currentStep === 3 && importResult && (
        <Card>
          <Result
            status="success"
            title="Import Complete!"
            subTitle={`${importResult.imported} events imported for ${course.course_id}`}
          >
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Imported">{importResult.imported}</Descriptions.Item>
              <Descriptions.Item label="Skipped">{importResult.skipped}</Descriptions.Item>
              <Descriptions.Item label="Errors">{importResult.errors}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={importResult.status === 'completed' ? 'green' : 'orange'}>
                  {importResult.status}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
            <div style={{ marginTop: 24 }}>
              <Space>
                <Button onClick={() => { setCurrentStep(0); setImportResult(null); setScanResult(null); }}>
                  Import More Files
                </Button>
                <Button type="primary" onClick={() => navigate('/courses')}>
                  Back to Courses
                </Button>
              </Space>
            </div>
          </Result>
        </Card>
      )}

      {/* Import History */}
      <Card title="Import History" style={{ marginTop: 24 }}>
        <Table
          dataSource={history}
          columns={historyColumns}
          rowKey="import_id"
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'No import history for this course' }}
        />
      </Card>
    </div>
  );
}
